import { concat, fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { htmlSafe } from '@ember/template';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { PrismDataBar, styleEntry } from '@mylighthouse/prism-ember';
import EmberTooltip from '@mylighthouse/prism-ember/components/ember-tooltip';
import { task } from 'ember-concurrency';
// @ts-expect-error: Incorrect type
import preventDefault from 'ember-event-helpers/helpers/prevent-default';
import { and, or } from 'ember-truth-helpers';
import {
  COLOR_PARITY_LOSS,
  COLOR_PARITY_MEET,
  COLOR_PARITY_WIN,
} from 'frontend-utils/utils/css-variable';
import GeneralButtonsButtonExpandArrow from 'lumen/components/general/buttons/button-expand-arrow';
import ParityTooltipsTooltipKpi from 'lumen/components/parity/tooltips/tooltip-kpi';
import type { HotelViewFilters } from 'lumen/controllers/market/parity/insight';
import type ParityInsightSegmentationItem from 'lumen/models/parity/insight/segmentation-item';
import { SEGMENTATION_VALUE_TYPES } from 'lumen/models/parity/insight/segmentation-item';
import {
  BREAKDOWN_BAR_THRESHOLD,
  PI_BREAKDOWNS,
  SORT_TYPES,
} from 'lumen/utils/constants/pi-constant-values';
import { isOtherRow } from 'lumen/utils/parity/datamodels/breakdown-item-other';
import { calculateBenchmarkPercentages } from 'lumen/utils/parity-analytics/calculate-parity-analytics-values';
import { canAddParityFilter } from 'lumen/utils/parity-analytics/can-add-parity-filter';
import roundParityPercentages from 'lumen/utils/parity-analytics/round-parity-percentages';
import tooltipSideCalculation from 'lumen/utils/tooltip-utils';
import { timeout } from 'lumen/utils/waiters';

import styles from './breakdown-value.module.css';

// Minimum percentage for win/meet/loss together
const MIN_PERCENTAGE = 3;
// PI amount of blocks per row
const AMOUNT_OF_BLOCKS_PER_ROW = 3;

const BLOCKS_WITH_BENCHMARK = [
  PI_BREAKDOWNS.SOURCE,
  PI_BREAKDOWNS.MARKET_ID,
  PI_BREAKDOWNS.CHANNEL,
  PI_BREAKDOWNS.CHANNEL_TYPE,
  PI_BREAKDOWNS.PLATFORM,
  PI_BREAKDOWNS.BRAND_ID,
];

interface ParityInsightBreakdownValueSignature {
  Args: {
    blockKey: string;
    data: ParityInsightSegmentationItem;
    depth: number;
    hideHotelsMetric: boolean;
    hotelViewFilters: HotelViewFilters;
    isDisabled: boolean;
    isSearchActive: boolean;
    maxNbOfShops: number;
    onSegmentationFilterClicked: (
      segmentationId: string,
      segmentationValueId: string,
    ) => void;
    parsedSegmentationFilters: {
      [key: string]: unknown[];
    };
    resetIndex: () => void;
    segmentationBlockIdx: number;
    setIsOtherRowCurrentlyExpanded: (
      isOtherRowCurrentlyExpanded: boolean,
    ) => void;
    sortType: string;
    triggerScrollbarUpdate: (isOtherRow: unknown) => void;
  };
}

/**
 * Optional
 * @param {Boolean} [splitBenchmark=false] - True if benchmark needs to split up in loss, meet and win
 */
export default class ParityInsightBreakdownValue extends Component<ParityInsightBreakdownValueSignature> {
  @tracked isHovered = false;
  // Shows the value as selected when the debounce has not yet completed
  @tracked willBeSelected = false;
  // Shows the value as not selected when the debounce has not yet completed
  @tracked willBeUnselected = false;

  debouncedHoverTask = task({ restartable: true }, async () => {
    await timeout('extra-short');
    this.isHovered = true;
  });

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get barData() {
    const percentages = this.args.data.calculatedValues;
    const roundedPercentages = roundParityPercentages(percentages);

    return [
      {
        widthPercentage: roundedPercentages.loss * 100,
        backgroundColor: `var(${COLOR_PARITY_LOSS})`,
        isHidden:
          roundedPercentages.loss * 100 * this.scaler <=
          BREAKDOWN_BAR_THRESHOLD,
      },
      {
        widthPercentage: roundedPercentages.meet * 100,
        backgroundColor: `var(${COLOR_PARITY_MEET})`,
        isHidden:
          roundedPercentages.meet * 100 * this.scaler <=
          BREAKDOWN_BAR_THRESHOLD,
      },
      {
        widthPercentage: roundedPercentages.win * 100,
        backgroundColor: `var(${COLOR_PARITY_WIN})`,
        isHidden:
          roundedPercentages.win * 100 * this.scaler <= BREAKDOWN_BAR_THRESHOLD,
      },
    ];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get barThresholdWidthPercentage() {
    const { scaler } = this;
    return Math.max(100 * scaler, MIN_PERCENTAGE);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get barValues() {
    const percentages = this.args.data.calculatedValues;
    const absValues = this.args.data.value;

    const benchmark = this.args.data.benchmark;
    const totalNbOfShops = this.args.data.calculatedValues?.totalNbOfShops;
    const totalNbOfHotels = this.args.data.value?.subscriptions;

    const { scaler } = this;

    // Total with for the win/meet/loss scaled to the block max nbOfShops
    const totalWidth =
      10000 * scaler > MIN_PERCENTAGE ? 10000 * scaler : MIN_PERCENTAGE;

    const roundedPercentages = roundParityPercentages(percentages);
    const roundedBenchmark = roundParityPercentages(
      calculateBenchmarkPercentages(benchmark),
    );

    return {
      // Absolute percentages against the value container (must be scaled to blockMax)
      total: htmlSafe(`width: ${totalWidth}%;`),
      totalNbOfShops,
      totalNbOfHotels,
      absValues,
      win: this.generateBarValue(roundedPercentages.win, scaler),
      meet: this.generateBarValue(roundedPercentages.meet, scaler),
      loss: this.generateBarValue(roundedPercentages.loss, scaler),
      benchmark:
        roundedBenchmark.totalNbOfShops === 0 ? null : roundedBenchmark,
    };
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get indentationAmount() {
    return this.args.depth * 10;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isDisabled() {
    if (this.showAsSelected) {
      return false;
    }

    return this.args.isDisabled;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isSelectable() {
    return !this.isDisabled && this.args.data.isSelectable;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isSelected() {
    const { data } = this.args;

    if (isOtherRow(data)) {
      return false;
    }

    let isSelected = false;

    if (this.args.parsedSegmentationFilters) {
      const parsedFilters = this.args.parsedSegmentationFilters;

      Object.keys(parsedFilters).forEach((filteredSegmentation) => {
        if (data.segmentationId === filteredSegmentation) {
          // check if the current segmentation value is selected (by comparing segmentationId & segmentationValueId)
          if (
            // @ts-expect-error: Incorrect type
            parsedFilters[filteredSegmentation].indexOf(
              data.segmentationValueId,
            ) !== -1
          ) {
            isSelected = true;
          } else if (data.parentId) {
            // Code to select children if parentId is selected. Need to change some strings to be able to compare
            // Example parentId: parent-travel_agent-EXPEDIA-filtered

            // Remove the filtered from the parentId (parent-travel_agent-EXPEDIA)
            const segValueParentId = data.parentId.replace('-filtered', '');

            // @ts-expect-error: Incorrect type
            const possibleParentIds = parsedFilters[filteredSegmentation]
              .filter(
                (segmentationName) =>
                  // Filter out the filter that might have children (check if parent- is present)
                  // @ts-expect-error: Incorrect type
                  // eslint-disable-next-line @typescript-eslint/no-unsafe-call
                  segmentationName.indexOf('parent-') !== -1,
              )
              .map(
                (segmentationName) =>
                  // Example segmentationName in this case: parent-EXPEDIA (passed like this in filters/URL)

                  // We try to form a string that will match the segValueParentId we created earlier
                  // segmentationName.substring(0, 7) => fixed to strip out 'parent-'
                  // filteredSegmentation => in this case travel_agent
                  // segmentationName.substring(7) => begins extraction at position 7 and extracts rest of the string. In this case 'EXPEDIA'
                  // End result: parent-travel_agent-EXPEDIA
                  `${
                    // @ts-expect-error: Incorrect type
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
                    segmentationName.substring(0, 7) + filteredSegmentation
                    // @ts-expect-error: Incorrect type
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
                  }-${segmentationName.substring(7)}`,
              );

            if (possibleParentIds.indexOf(segValueParentId) !== -1) {
              isSelected = true;
            }
          }
        }
      });
    }

    return isSelected;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get noBenchmarkForBlock() {
    const block = this.args.blockKey;
    // @ts-expect-error: Incorrect type
    return BLOCKS_WITH_BENCHMARK.indexOf(block) === -1;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get scaler() {
    const blockMax = this.args.maxNbOfShops;
    const totalNbOfShops = this.args.data?.calculatedValues?.totalNbOfShops;

    // Scaler is the factor of scale of this value's number of shops against the block's max number of shops
    let scaler = 0;

    // Protect against divide-by-zero
    if (totalNbOfShops !== undefined && totalNbOfShops > 0) {
      scaler = totalNbOfShops / blockMax;
    }

    // Protect against widths over 100%
    // OR whenever we don't want to scale bars by absolute values
    if (scaler > 1 || this.args.sortType === SORT_TYPES.RELATIVE) {
      scaler = 1;
    }
    return scaler;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get showAsSelected() {
    return (this.isSelected || this.willBeSelected) && !this.willBeUnselected;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get showExpandableArrow() {
    return (
      this.args.data?.segmentationValueType ===
        SEGMENTATION_VALUE_TYPES.PARENT ||
      this.args.data?.segmentationValueType === SEGMENTATION_VALUE_TYPES.OTHER
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get tooltipSide() {
    const indexOfSegmentationItem = this.args.segmentationBlockIdx;
    return tooltipSideCalculation(
      indexOfSegmentationItem,
      AMOUNT_OF_BLOCKS_PER_ROW,
    );
  }

  // @ts-expect-error: Incorrect type
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  generateBarValue(percentage, scaler) {
    return {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      value: percentage,
      absolute: {
        // Absolute percentages represent the width scaled by the maxBlock shops
        cssWidth: htmlSafe(`width: ${percentage * 100 * scaler}%;`),
        width: percentage * 100 * scaler,
      },
      relative: {
        // Relative percentages always add up to 100 %
        cssWidth: htmlSafe(`width: ${percentage}%;`),
      },
    };
  }

  @action
  onMouseLeave(): void {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    this.debouncedHoverTask.cancelAll();
    this.isHovered = false;
  }

  @action
  // @ts-expect-error: Incorrect type
  segmentationFilterClicked(objSegmentationValue): void {
    if (this.args.data?.isSelectable) {
      if (this.isSelected) {
        this.willBeUnselected = true;
      } else if (
        canAddParityFilter(
          // @ts-expect-error: Incorrect type
          this.args.parsedSegmentationFilters,
          this.args.data?.segmentationId,
        )
      ) {
        this.willBeSelected = true;
      }
      // Should only rely on segmentationId and segmentationValueId
      this.args.onSegmentationFilterClicked(
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
        objSegmentationValue.segmentationId,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
        objSegmentationValue.segmentationValueId,
      );
    }
  }

  @action
  toggleExpandedRow(): void {
    this.args.data.isExpanded = !this.args.data?.isExpanded;

    if (isOtherRow(this.args.data) && this.args.data.isExpanded) {
      this.args.setIsOtherRowCurrentlyExpanded(true);
    } else {
      this.args.setIsOtherRowCurrentlyExpanded(false);
      this.args.resetIndex();
    }

    // This used to be scheduled after render, but after a template refactor, that was no longer needed and caused issues
    this.args.triggerScrollbarUpdate(isOtherRow(this.args.data));
  }

  <template>
    {{! @glint-nocheck }}
    {{! template-lint-disable require-presentational-children }}
    <div
      class="flex
        {{if this.showAsSelected styles.item--selected}}
        {{if this.isSelectable styles.item--selectable}}
        {{if this.isDisabled styles.item--disabled}}"
      style={{styleEntry "padding-left" (concat this.indentationAmount "px")}}
    >
      {{#if this.showExpandableArrow}}
        <div
          role="button"
          {{on "click" (preventDefault this.toggleExpandedRow)}}
        >
          <GeneralButtonsButtonExpandArrow @isExpanded={{@data.isExpanded}} />
        </div>
      {{/if}}
      <button
        class="button--unstyled
          {{styles.button}}
          {{@data.segmentationId}}_value
          {{if
            (and
              this.isSelectable
              (or
                (canAddParityFilter
                  @parsedSegmentationFilters @data.segmentationId
                )
                this.showAsSelected
              )
            )
            styles.item--selectable
          }}"
        disabled={{this.isDisabled}}
        type="button"
        {{on
          "click"
          (preventDefault (fn this.segmentationFilterClicked @data))
        }}
        {{on "mouseenter" this.debouncedHoverTask.perform}}
        {{on "mouseleave" this.onMouseLeave}}
      >
        <PrismDataBar
          @data={{this.barData}}
          @thresholdWidthPercentage={{this.barThresholdWidthPercentage}}
          @valuePosition="bottom"
          as |DataBar|
        >
          <DataBar.label>
            <p class="text-200--regular truncate">
              {{@data.formattedSegmentationValue}}
            </p>
          </DataBar.label>

          <DataBar.value @text={{this.barValue}} />
        </PrismDataBar>
        {{#if this.isHovered}}
          <EmberTooltip
            @isShown={{this.isHovered}}
            @side={{this.tooltipSide}}
            @tooltipClass="base-tooltip parity-analytics-segmentation-value-tooltip"
          >
            <ParityTooltipsTooltipKpi
              @data={{this.barValues}}
              @displayName={{@data.formattedSegmentationValue}}
              @hideShoppedHotels={{@hideHotelsMetric}}
              @noBenchmarkForBlock={{this.noBenchmarkForBlock}}
              @splitBenchmark={{true}}
            />
          </EmberTooltip>
        {{/if}}
      </button>
    </div>
  </template>
}
